/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';
import { Dispatcher } from 'src/core/types/dispatcher';
import { HandlerGroup, HandlerGroupFactory } from 'src/core/types/handler';

/**
 * A hook to install handler groups to a dispatcher in from a certain
 * component. The beauty of this approach is that now handler groups
 * you install using this hook have life spans that are tied to the
 * component's life span as well, due to the clean on the `useEffect`
 * React hook.
 *
 * @param dispatcher The dispatcher to use.
 * @param handlerGroupFactories An array of handler group factories
 * used to create the handler groups that will be installed.
 */
export const useInstallHandlerGroup = <T extends HandlerGroup>(
    dispatcher: Dispatcher<T>,
    ...handlerGroupFactories: readonly HandlerGroupFactory[]
): void => {
    const handlerGroupDisposers = React.useRef<(() => void)[]>([]);

    // TODO: See if we can replace this with having React.useLayoutEffect
    if (!handlerGroupDisposers.current.length) {
        handlerGroupDisposers.current = handlerGroupFactories.map(dispatcher.installHandlerGroup);
    }

    React.useEffect(() => {
        if (!handlerGroupDisposers.current.length) {
            handlerGroupDisposers.current = handlerGroupFactories.map(dispatcher.installHandlerGroup);
        }

        return () => {
            handlerGroupDisposers.current.forEach(disposer => disposer?.());
            handlerGroupDisposers.current = [];
        };
    }, [dispatcher, ...handlerGroupFactories]);
};
